import React, { useState, useEffect } from "react";
import { Header } from "../../Header";
import CircularProgress from "@mui/material/CircularProgress";
import "./Customer.css";
import { local_url } from "../../Const";

// Function to format date for input and API
const formatDateForInput = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDateForAPI = (date) => date.replace(/-/g, "");

const formatDateTime = (dateTime, formatType) => {
  if (!dateTime) return "-";
  const date = new Date(dateTime);
  if (formatType === "date") {
    return `${String(date.getDate()).padStart(2, "0")}-${String(
      date.getMonth() + 1
    ).padStart(2, "0")}-${date.getFullYear()}`;
  }
  return "-";
};

export const GTStockMovement = () => {
  const today = new Date();
  const sixtyDaysAgo = new Date(today);
  sixtyDaysAgo.setDate(today.getDate() - 10);

  const [ledgerData, setLedgerData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fromDate, setFromDate] = useState(formatDateForInput(sixtyDaysAgo));
  const [toDate, setToDate] = useState(formatDateForInput(today));
  const [searchQuery, setSearchQuery] = useState("");

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc", // or "desc"
  });

  const formattedFromDateForAPI = formatDateForAPI(fromDate);
  const formattedToDateForAPI = formatDateForAPI(toDate);

  const API_URL = `${local_url}/api/GTSaleMovementReport/getGTStockMovementListing/L111/All/${formattedFromDateForAPI}/${formattedToDateForAPI}`;

  const fetchLedgerData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(API_URL);
      if (!response.ok) throw new Error("Network response was not ok");
      const data = await response.json();

      // Sort the data by batchNumber initially
      const sortedData = data.sort((a, b) => {
        const batchA = a.batchNumber.toLowerCase();
        const batchB = b.batchNumber.toLowerCase();
        return batchA.localeCompare(batchB);
      });

      setLedgerData(sortedData);
      setFilteredData(sortedData);
    } catch (error) {
      setError("Failed to fetch ledger data. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (setter) => (event) => {
    setter(event.target.value);
  };

  const handleSearch = (event) => {
    const query = event.target.value.toLowerCase();
    setSearchQuery(query);

    const filtered = ledgerData.filter((item) =>
      item.itemName.toLowerCase().includes(query) ||
      item.batchNumber.toLowerCase().includes(query)
    );

    setFilteredData(filtered);
  };

  const handleSort = (columnKey) => {
    let direction = "asc";
    if (sortConfig.key === columnKey && sortConfig.direction === "asc") {
      direction = "desc";
    }

    const sortedData = [...filteredData].sort((a, b) => {
      if (a[columnKey] === null || a[columnKey] === undefined) return 1;
      if (b[columnKey] === null || b[columnKey] === undefined) return -1;
      if (a[columnKey] < b[columnKey]) return direction === "asc" ? -1 : 1;
      if (a[columnKey] > b[columnKey]) return direction === "asc" ? 1 : -1;
      return 0;
    });

    setFilteredData(sortedData);
    setSortConfig({ key: columnKey, direction });
  };

  const exportToCSV = () => {
    const csvRows = [];
    const headers = [
      "Item Name",
      "Batch Number",
      "From Date",
      "To Date",
      "MRP",
      "GT Sale Quantity",
      "GT Return Quantity",
      "P-Shop Quantity",
    ];
    csvRows.push(headers.join(","));

    ledgerData.forEach((item) => {
      const fromDate = formatDateTime(item.fromDate, "date");
      const toDate = formatDateTime(item.todate, "date");

      const row = [
        item.itemName,
        item.batchNumber,
        fromDate,
        toDate,
        item.mrp.toFixed(2),
        item.gtSaleQty,
        item.gtReturnQty,
        item.pShopQty,
      ];
      csvRows.push(row.join(","));
    });

    const csvContent = "data:text/csv;charset=utf-8," + csvRows.join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "gt_stock_movement_report.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    fetchLedgerData();
  }, []);

  return (
    <>
      <Header />
      <div className="customer-container">
        <h1 className="customer-header">GT Stock Movement Report</h1>
        {error && <div className="customer-error-message">{error}</div>}
        <div className="customer-date-input-container">
          <input
            type="date"
            value={fromDate}
            onChange={handleDateChange(setFromDate)}
            max={formatDateForInput(today)}
            className="customer-date-input"
          />
          <div style={{ margin: "3px" }}>
            <span>-</span>
          </div>
          <input
            type="date"
            value={toDate}
            onChange={handleDateChange(setToDate)}
            max={formatDateForInput(today)}
            className="customer-date-input"
          />
          <button
            onClick={fetchLedgerData}
            className="customer-submit-button"
            disabled={loading}
          >
            Submit
          </button>
          <button
            onClick={exportToCSV}
            className="customer-export-button"
            disabled={loading || ledgerData.length === 0}
          >
            Export to CSV
          </button>
        </div>

        <div className="customer-search-container">
          <input
            type="text"
            placeholder="Search Item Name"
            value={searchQuery}
            onChange={handleSearch}
            className="customer-search-input"
          />
        </div>

        <div className="customer-table-container">
          <div className="customer-table-scroll">
            <table className="customer-table">
              <thead>
                <tr>
                  <th>S No.</th> {/* This is the serial number column */}
                  <th onClick={() => handleSort("itemName")}>
                    Item Name {sortConfig.key === "itemName" && (sortConfig.direction === "asc" ? "↑" : "↓")}
                  </th>
                  <th onClick={() => handleSort("batchNumber")}>
                    Batch Number {sortConfig.key === "batchNumber" && (sortConfig.direction === "asc" ? "↑" : "↓")}
                  </th>
                  <th onClick={() => handleSort("fromDate")}>
                    From Date {sortConfig.key === "fromDate" && (sortConfig.direction === "asc" ? "↑" : "↓")}
                  </th>
                  <th onClick={() => handleSort("toDate")}>
                    To Date {sortConfig.key === "toDate" && (sortConfig.direction === "asc" ? "↑" : "↓")}
                  </th>
                  <th onClick={() => handleSort("mrp")}>
                    MRP {sortConfig.key === "mrp" && (sortConfig.direction === "asc" ? "↑" : "↓")}
                  </th>
                  <th onClick={() => handleSort("gtSaleQty")}>
                    GT Sale Quantity {sortConfig.key === "gtSaleQty" && (sortConfig.direction === "asc" ? "↑" : "↓")}
                  </th>
                  <th onClick={() => handleSort("gtReturnQty")}>
                    GT Return Quantity {sortConfig.key === "gtReturnQty" && (sortConfig.direction === "asc" ? "↑" : "↓")}
                  </th>
                  <th onClick={() => handleSort("pShopQty")}>
                    P-Shop Quantity {sortConfig.key === "pShopQty" && (sortConfig.direction === "asc" ? "↑" : "↓")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="9" className="customer-loading-container">
                      <CircularProgress />
                    </td>
                  </tr>
                ) : filteredData.length > 0 ? (
                  filteredData.map((item, index) => {
                    const fromDate = formatDateTime(item.fromDate, "date");
                    const toDate = formatDateTime(item.todate, "date");

                    return (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td>{item.itemName}</td>
                        <td>{item.batchNumber}</td>
                        <td>{fromDate}</td>
                        <td>{toDate}</td>
                        <td>₹{item.mrp.toFixed(2)}</td>
                        <td>{item.gtSaleQty}</td>
                        <td>{item.gtReturnQty}</td>
                        <td>{item.pShopQty}</td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="9" className="customer-no-data">
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
};

export default GTStockMovement;
